import React, { useEffect, useCallback, useState, useMemo } from "react";
import { Box, Tooltip, Link, Typography, TextField, InputAdornment, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { RootState } from "../../../../store/store";
import { getClaimsData, GetClaimTransactions, GetClaimTransactionDocumentContent } from "../../DashboardAction";
import { getCookie, isFeatureAvailable, getBufferData } from "../../../../utils/Utility";
import { ClaimsStyles } from "../ClaimsStyles";
import { ClaimListStyles } from "./ClaimListStyles";
import { useTranslation } from "react-i18next";
import CustomTable, { IColumn } from "../../../../common/CustomTable/CustomTable";
import moment from "moment";
import { dateFormat, featuresCodes } from "../../../../constants/Constants";
import { IClaim, IGetClaimTransactionDocumentContentRequestParams } from "../../../../models/user/dashboard/Dashboard";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import ClaimTracking from "../../../../Agents/ClaimTracking/ClaimTracking";
import { Timeline, timelineOppositeContentClasses } from "@mui/lab";
import CustomTimeline from "../../../../common/CustomTimeline/CustomTimeline";
import Document from "../../../PolicyDetails/policyTabComponents/Documents/Document/Document";
import { IDocument } from "../../../../models/user/policyDetails/Document";
import { clearClaimsData, resetDashboardState, setClaimTransactionDocumentContent } from "../../DashboardSlice";

const ClaimsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { claimsData } = useAppSelector((store: RootState) => store.dashboard);
  const {
    claimTransactions,
    claimDocuments,
    claimTransactionDocumentContent,
    carrierClaimStatus,
    initiatedDate,
    carrierPolicyNo,
    claimDescription,
    updateDate,
  } = useAppSelector((state: RootState) => state.dashboard);
  const { features } = useAppSelector((state: RootState) => state.features);
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (store: RootState) => store.common
  );

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  // Filter states
  const [policyNumberFilter, setPolicyNumberFilter] = useState("");
  const [claimNumberFilter, setClaimNumberFilter] = useState("");
  const [claimActivityDialogOpen, setClaimActivityDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<IClaim | null>(null);
  const [currentClickFile, setCurrentClickFile] = useState<IDocument | null>(null);

  const handleNavigate = useCallback(() => {
    navigate("/customer/dashboard");
  }, [navigate]);

  // Clear claims data when user or system changes
  useEffect(() => {
    dispatch(resetDashboardState());
  }, [userDetails?.customerId, carrier, system, dispatch]);

  // Clear claims data on component mount
  useEffect(() => {
    dispatch(resetDashboardState());
    
    // Cleanup function to clear data when component unmounts
    return () => {
      dispatch(resetDashboardState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (userDetails && userDetails.customerId && carrier && system) {
      dispatch(
        getClaimsData({
          customerId: userDetails.customerId,
          carrier,
          system,
        })
      );
    }
  }, [userDetails, dispatch, carrier, system]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle document download (same logic as ClaimCard)
  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "dashboard/GetClaimTransactionDocumentContent" &&
      claimTransactionDocumentContent &&
      currentClickFile
    ) {
      const bufferArray = getBufferData(claimTransactionDocumentContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );

      const downloadLink = document.createElement("a");
      downloadLink.download = `${currentClickFile?.name}-${currentClickFile?.addDate}-${currentClickFile?.addTime}.pdf`;
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }, [
    calledReducerType,
    isSuccess,
    claimTransactionDocumentContent,
    currentClickFile,
  ]);

  useEffect(() => {
    if (
      isError &&
      calledReducerType === "dashboard/GetClaimTransactionDocumentContent"
    ) {
      alert(`Error in downloading document`);
    }
  }, [calledReducerType, isError]);

  // Filter claims based on search criteria
  const filteredClaims = useMemo(() => {
    if (!claimsData?.claimList) return [];
    
    return claimsData.claimList.filter((claim: IClaim) => {
      const policyMatch = claim.carrierPolicyNo?.toLowerCase().includes(policyNumberFilter.toLowerCase()) || !policyNumberFilter;
      const claimMatch = claim.carrierClaimNo?.toLowerCase().includes(claimNumberFilter.toLowerCase()) || !claimNumberFilter;
      return policyMatch && claimMatch;
    });
  }, [claimsData?.claimList, policyNumberFilter, claimNumberFilter, userDetails?.customerId, carrier, system]);

  // Helper function to get feature codes (same as ClaimCard)
  const getFeatureCodes = (featureCode: string) => {
    if (!featureCode || !selectedClaim?.lobCode) {
      return;
    }
    const name: any = featuresCodes[featureCode as keyof typeof getFeatureCodes];
    return name[selectedClaim.lobCode];
  };

  // Handle claim action (same logic as ClaimCard)
  const handleClaimAction = (claim: any) => {
    setSelectedClaim(claim);
    if (claim.claimId && claim.carrier && claim.system && claim.lobCode) {
      dispatch(
        GetClaimTransactions({ 
          claimId: claim.claimId, 
          carrier: claim.carrier, 
          system: claim.system, 
          lobCode: claim.lobCode 
        })
      ).then(() => setClaimActivityDialogOpen(true));
    }
  };

  // Handle file download (same logic as ClaimCard)
  const handleFileClick = (type: string, documentData: IDocument) => {
    dispatch(setClaimTransactionDocumentContent(null));
    setCurrentClickFile(documentData);
    const requestParams: IGetClaimTransactionDocumentContentRequestParams = {
      claimId: selectedClaim?.claimId || "",
      documentId: "Claims_Document",
    };
    dispatch(GetClaimTransactionDocumentContent(requestParams));
  };

  // Table columns configuration
  const columns: IColumn[] = [
    {
      label: "Stage",
      accessorKey: "carrierClaimStatus",
      styles: { width: "120px" }
    },
    {
      label: "Claim #",
      accessorKey: "carrierClaimNo",
      styles: { width: "100px" }
    },
    {
      label: "Policy #",
      accessorKey: "carrierPolicyNo",
      styles: { width: "150px" }
    },
    {
      label: "Updated On",
      accessorKey: "updateDate",
      styles: { width: "120px" },
      Cell: (claim: any) => (
        <Typography>
          {moment(claim.updateDate).format(dateFormat)}
        </Typography>
      )
    },
    {
      label: "Initiated On",
      accessorKey: "initiatedDate",
      styles: { width: "120px" },
      Cell: (claim: any) => (
        <Typography>
          {moment(claim.initiatedDate).format(dateFormat)}
        </Typography>
      )
    },
    {
      label: "Contact Person",
      accessorKey: "adjusterDetails",
      styles: { width: "150px" },
      Cell: (claim: any) => (
        <Typography>
          {claim.adjusterDetails?.nameInfo?.includes(",")
            ? claim.adjusterDetails.nameInfo.split(",")[1]
            : claim.adjusterDetails?.nameInfo || "-"}
        </Typography>
      )
    },
    {
      label: "Action",
      styles: { width: "150px" },
      Cell: (claim: any) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleClaimAction(claim)}
        >
          {claim.carrierClaimStatus === "Closed" 
            ? t("View Details") 
            : t("Track your claim")}
        </Button>
      )
    }
  ];

  return (
    <Box sx={ClaimsStyles.allClaimsListContainer}>
      <Box sx={ClaimListStyles.headerStyles} onClick={handleNavigate}>
        <Link 
          id="dashboard-link"
          data-test-id="test-dashboard-link"
          aria-label="Back to Dashboard Link"
          sx={ClaimsStyles.link}
        >
          <Tooltip title="Go to Dashboard">
            <KeyboardArrowLeftIcon 
              id="back-arrow-icon"
              data-test-id="test-back-arrow-icon"
              aria-label="Back Arrow Icon"
            />
          </Tooltip>
        </Link>
        <Typography
          id="back-to-dashboard-text"
          data-test-id="back-to-dashboard-text"
          aria-label="Back to Dashboard"
        >
          {t("Back to Dashboard")}
        </Typography>
      </Box>

      {/* Filter Section */}
      <Box sx={{ 
        display: "flex", 
        gap: 2, 
        mb: 3, 
        flexWrap: "wrap",
        "@media (max-width: 768px)": {
          flexDirection: "column"
        }
      }}>
        <TextField
          id="policy-number-filter"
          data-test-id="test-policy-number-filter"
          aria-label="Filter by Policy Number"
          placeholder={t("Search by Policy Number")}
          value={policyNumberFilter}
          onChange={(e) => setPolicyNumberFilter(e.target.value)}
          size="small"
          sx={{ minWidth: "200px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          id="claim-number-filter"
          data-test-id="test-claim-number-filter"
          aria-label="Filter by Claim Number"
          placeholder={t("Search by Claim Number")}
          value={claimNumberFilter}
          onChange={(e) => setClaimNumberFilter(e.target.value)}
          size="small"
          sx={{ minWidth: "200px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Claims Table */}
      <Box sx={{ width: "100%" }}>
        {filteredClaims.length > 0 ? (
          <CustomTable
            columns={columns}
            data={filteredClaims}
            pagination={true}
            rowsInPage={10}
            styles={{
              width: "100%",
              overflow: "auto"
            }}
          />
        ) : (
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            minHeight: "200px" 
          }}>
            <Typography
              id="no-claims-text"
              data-test-id="test-no-claims-text"
              aria-label="No Claims Available"
              sx={{ color: "text.secondary" }}
            >
              {policyNumberFilter || claimNumberFilter 
                ? t("No claims match your search criteria") 
                : t("No Claims Available")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Claim Details Drawer - Using shared component */}
      {selectedClaim && (
        <CustomDrawer
          open={claimActivityDialogOpen}
          onClose={() => setClaimActivityDialogOpen(false)}
          title={`${selectedClaim.carrierClaimNo} - ${selectedClaim.carrierClaimStatus}`}
          content={
            <Box sx={{ display: "grid", gap: "20px" }}>
              {getCookie("roleCode") === "Producer" ? (
                <ClaimTracking
                  carrierClaimStatus={carrierClaimStatus}
                  initiatedDate={initiatedDate}
                  carrierPolicyNo={carrierPolicyNo}
                  claimDescription={claimDescription}
                  updateDate={updateDate}
                  claim={selectedClaim}
                  claimTransactions={claimTransactions}
                />
              ) : (
                <Box sx={{ display: "grid", gap: "10px" }}>
                  <Typography
                    sx={{
                      color: "#383D4E",
                      fontSize: "20px",
                      fontWeight: 400,
                      fontFamily: "Playfair Display",
                    }}
                  >
                    {t("Transactions")}
                  </Typography>
                  {claimTransactions.length > 0 ? (
                    <Timeline
                      sx={{
                        [`& .${timelineOppositeContentClasses.root}`]: {
                          flex: 0.2,
                        },
                        zIndex: 1,
                      }}
                    >
                      {claimTransactions.map(
                        (item: any, index: number) => (
                          <CustomTimeline
                            key={`claim-transaction-${index}`}
                            oppositeContent={
                              <Box
                                sx={{
                                  display: "flex",
                                  height: "100%",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Typography>
                                  {moment(new Date(item.transactionDate)).format(
                                    dateFormat
                                  )}
                                </Typography>
                              </Box>
                            }
                            oppositeContentStyle={{
                              color: "#1D252B",
                              fontSize: "18px",
                              fontFamily: "Noto Sans",
                              fontWeight: "300",
                              "@media(max-width:599px)": {
                                fontSize: "0.8rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontFamily: "Noto Sans",
                              },
                              "@media (min-width: 1025px) and (max-width: 1366px)":
                                {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#1D252B",
                                  fontSize: "18px",
                                  fontFamily: "Noto Sans",
                                  fontWeight: "300",
                                  textOverflow: "ellipsis",
                                  fontStyle: "normal",
                                  lineHeight: "110%",
                                  "@media (max-width:599px)": {
                                    fontSize: "0.8rem",
                                  },
                                }}
                              >
                                {item.transactionDescription}
                              </Typography>
                            </Box>
                          </CustomTimeline>
                        )
                      )}
                    </Timeline>
                  ) : (
                    <Typography
                      sx={{
                        color: "#1D252B",
                        fontSize: "18px",
                        fontFamily: "Noto Sans",
                        fontWeight: "300",
                        "@media(max-width:599px)": {
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: '"Noto Sans"',
                        },
                        "@media (min-width: 1025px) and (max-width: 1366px)": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    >
                      {t("No transactions found")}
                    </Typography>
                  )}
                </Box>
              )}
              {isFeatureAvailable({
                featureCodes: getFeatureCodes("Documents"),
                features,
              }) ? (
                <Box sx={{ display: "grid", gap: "10px" }}>
                  <Typography
                    sx={{
                      color: "#383D4E",
                      fontSize: "20px",
                      fontWeight: 400,
                      fontFamily: "Playfair Display",
                    }}
                  >
                    {t("Documents")}
                  </Typography>
                  <Box>
                    {claimDocuments.length > 0 ? (
                      claimDocuments.map((document, index) => (
                        <Document
                          key={`doc-list-${index}`}
                          documentData={document}
                          handleFileClick={handleFileClick}
                          view={false}
                        />
                      ))
                    ) : (
                      <Typography
                        sx={{
                          color: "#1D252B",
                          fontSize: "18px",
                          fontFamily: "Noto Sans",
                          fontWeight: "300",
                          "@media(max-width:599px)": {
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontFamily: '"Noto Sans"',
                          },
                          "@media (min-width: 1025px) and (max-width: 1366px)": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      >
                        {t("No Documents Available")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <></>
              )}
            </Box>
          }
          drawerStyles={{
            "& .MuiDrawer-paper": {
              padding: "0 20px 20px 20px",
              width: "50%",
              "@media (max-width:768px)": {
                width: "100%",
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default ClaimsList;
